import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ContractsService } from '../../../services/contracts.service';
import { VehiclesService } from '../../../services/vehicles.service';
import { ProductsService } from '../../../services/products.service';
import { Subject, forkJoin } from 'rxjs';
import { ErrorService } from '../../../shared/services/error.service';
import { Vehicle } from '../../../models/Vehicle';
import { Product } from '../../../models/Product';
import { map, filter, switchMap, tap, finalize, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-contract-replace-vehicle',
  templateUrl: './contract-replace-vehicle.component.html',
  styleUrls: ['./contract-replace-vehicle.component.css']
})
export class ContractReplaceVehicleComponent implements OnInit, OnDestroy {
  replacementForm: FormGroup;
  contractId!: number;
  isLoading = false;
  isSaving = false;
  
  contractData: any = null;
  contract: any = null;
  driver: any = null;
  vehicleSnapshot: any = null;
  product: Product | null = null;
  products: Product[] = [];
  selectedProduct: Product | null = null;
  contractWeeksCount = 4;
  contractWeeklyAmount = 0;
  
  // Veículo de substituição
  vehicles: Vehicle[] = []; // mantido apenas para compatibilidade, mas não mais usado na UI
  selectedVehicle: Vehicle | null = null;
  isSearchingVehicle = false;
  
  private destroy$ = new Subject<void>();
  
  constructor(
    private fb: FormBuilder,
    private contractsService: ContractsService,
    private vehiclesService: VehiclesService,
    private productsService: ProductsService,
    private router: Router,
    private route: ActivatedRoute,
    private errorService: ErrorService
  ) {
    this.replacementForm = this.fb.group({
      productId: [null, Validators.required],
      vehicleId: ['', Validators.required],
      vehiclePlate: ['', Validators.required],
      vehicleModel: ['', Validators.required],
      vehicleYear: [null, Validators.required],
      vehicleColor: [''],
      vehicleBrand: [''],
      vehicleRenavam: [''],
      vehicleChassis: [''],
      vehicleCurrentKm: [null],
      vehicleInspectionNotes: [''],
      
      driverName: ['', Validators.required],
      driverCpfCnpj: ['', Validators.required],
      driverCnhNumber: [''],
      driverCnhExpiryDate: [''],
      driverEmail: [''],
      driverPhone: [''],
      driverStreet: ['', Validators.required],
      driverNumber: ['', Validators.required],
      driverComplement: [''],
      driverDistrict: ['', Validators.required],
      driverCity: ['', Validators.required],
      driverState: ['', Validators.required],
      driverZipCode: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.route.params.pipe(
      map(params => Number(params['id'])),
      filter(id => !!id),
      tap(id => { 
        this.contractId = id; 
        this.isLoading = true; 
      }),
      switchMap(id =>
        this.contractsService.getContractComplete(id).pipe(
          finalize(() => (this.isLoading = false))
        )
      ),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (contractData) => {
        this.contractData = contractData;
        this.contract = contractData?.contract || null;
        this.driver = contractData?.driver || null;
        this.vehicleSnapshot = contractData?.vehicleSnapshot || contractData?.vehicle || null;

        if (this.contract?.productId) {
          // Carregar lista de produtos para permitir upgrade/troca de plano
          this.productsService.getProducts().subscribe({
            next: (products) => {
              this.products = (products || []).filter(p => p.isActive);
              const current = this.products.find(p => p.id === this.contract.productId) || null;
              this.product = current;
              this.selectedProduct = current;
              this.replacementForm.patchValue({
                productId: current?.id ?? this.contract.productId
              }, { emitEvent: false });
              this.recalculateProductPreview();
            },
            error: () => {
              this.products = [];
              this.product = null;
            }
          });
        }

        this.populateForm();
      },
      error: (error) => {
        this.errorService.showErrorMessage('Erro ao carregar dados: ' + (error?.message || 'Erro desconhecido'));
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  populateForm(): void {
    if (this.driver) {
      const address = this.driver.address || this.driver;
      
      this.replacementForm.patchValue({
        driverName: this.driver.name || '',
        driverCpfCnpj: this.driver.cpfCnpj || '',
        driverCnhNumber: this.driver.cnhNumber || '',
        driverCnhExpiryDate: this.formatDateForInput(this.driver.cnhExpiryDate) || '',
        driverEmail: this.driver.email || '',
        driverPhone: this.driver.phone || '',
        driverStreet: address.street || '',
        driverNumber: address.number || '',
        driverComplement: address.complement || '',
        driverDistrict: address.district || '',
        driverCity: address.city || '',
        driverState: address.state || '',
        driverZipCode: address.zipCode || ''
      });
    }
  }

  onProductChange(): void {
    const productId = this.replacementForm.get('productId')?.value;
    if (!productId || !this.products.length) {
      this.selectedProduct = null;
      this.contractWeeksCount = 4;
      this.contractWeeklyAmount = 0;
      return;
    }
    const idNum = Number(productId);
    this.selectedProduct = this.products.find(p => p.id === idNum) || null;
    this.product = this.selectedProduct;
    this.recalculateProductPreview();
  }

  private recalculateProductPreview(): void {
    if (!this.selectedProduct) {
      this.contractWeeksCount = 4;
      this.contractWeeklyAmount = 0;
      return;
    }

    const days = Number(this.selectedProduct.days) || 0;
    let parcelas = Math.floor(days / 7);
    if (parcelas < 1) parcelas = 1;

    this.contractWeeksCount = parcelas;
    const base = Number(this.selectedProduct.basePrice) || 0;
    this.contractWeeklyAmount = base;
  }

  /**
   * Busca veículo pela placa digitada e preenche os campos do veículo de substituição.
   */
  searchVehicleByPlate(): void {
    const rawPlate = (this.replacementForm.get('vehiclePlate')?.value || '').toString().trim();
    if (!rawPlate) {
      this.errorService.showErrorMessage('Informe a placa do veículo para buscar.');
      return;
    }

    const plate = rawPlate.replace(/\s+/g, '').toUpperCase();
    this.isSearchingVehicle = true;

    this.vehiclesService.getByPlate(plate).pipe(
      finalize(() => (this.isSearchingVehicle = false))
    ).subscribe({
      next: (vehicle) => {
        if (!vehicle) {
          this.errorService.showErrorMessage(`Nenhum veículo encontrado com a placa ${plate}.`);
          return;
        }

        this.selectedVehicle = vehicle;

        this.replacementForm.patchValue({
          vehicleId: vehicle.id,
          vehiclePlate: vehicle.plateNumber || plate,
          vehicleModel: vehicle.model || '',
          vehicleYear: vehicle.modelYear || null,
          vehicleColor: vehicle.color || '',
          vehicleBrand: vehicle.brand || '',
          vehicleRenavam: vehicle.renavam || '',
          vehicleChassis: vehicle.chassisNumber || '',
          vehicleCurrentKm: vehicle.currentMileage || 0
        });

        this.errorService.showInfoMessage('Dados do veículo carregados pela placa.');
      },
      error: (err) => {
        this.errorService.handleHttpError(err, 'Erro ao buscar veículo pela placa');
      }
    });
  }

  onSubmit(): void {
    if (this.replacementForm.invalid) {
      this.errorService.showErrorMessage('Preencha todos os campos obrigatórios');
      return;
    }
    
    this.replaceVehicle();
  }

  replaceVehicle(): void {
    this.isSaving = true;
    const formValue = this.replacementForm.value;

    const replacementData = {
      productId: formValue.productId || this.contract?.productId || null,
      vehicleSnapshot: {
        plate: formValue.vehiclePlate,
        model: formValue.vehicleModel,
        year: formValue.vehicleYear,
        color: formValue.vehicleColor || '',
        brand: formValue.vehicleBrand || '',
        renavam: formValue.vehicleRenavam || '',
        chassis: formValue.vehicleChassis || '',
        currentKm: formValue.vehicleCurrentKm || 0,
        inspectionNotes: formValue.vehicleInspectionNotes || ''
      },
      driver: {
        name: formValue.driverName,
        cpfCnpj: formValue.driverCpfCnpj,
        cnhNumber: formValue.driverCnhNumber || '',
        cnhExpiryDate: this.formatDateForAPI(formValue.driverCnhExpiryDate) || '',
        email: formValue.driverEmail || '',
        phone: formValue.driverPhone || '',
        street: formValue.driverStreet,
        number: formValue.driverNumber,
        complement: formValue.driverComplement || '',
        district: formValue.driverDistrict,
        city: formValue.driverCity,
        state: formValue.driverState,
        zipCode: formValue.driverZipCode
      }
    };

    this.contractsService.replaceVehicle(this.contractId, replacementData).pipe(
      finalize(() => (this.isSaving = false))
    ).subscribe({
      next: (response) => {
        this.errorService.showInfoMessage(response?.message || 'Veículo substituído com sucesso!');
        this.router.navigate(['/contracts', this.contractId, 'view']);
      },
      error: (err) => {
        this.errorService.showErrorMessage('Erro ao substituir veículo: ' + (err?.message || 'Erro desconhecido'));
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/contracts', this.contractId, 'view']);
  }

  formatPrice(value: number): string {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  formatDateForInput(date: string | Date): string {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  formatDateForAPI(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00Z');
    return date.toISOString();
  }
}

